import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productService, Product, Category } from '@/services/product-service'
import { toast } from 'sonner'

export function useProducts() {
    const queryClient = useQueryClient()

    const productsQuery = useQuery({
        queryKey: ['products'],
        queryFn: () => productService.getProducts({ limit: 1000 }),
    })

    const categoriesQuery = useQuery({
        queryKey: ['categories'],
        queryFn: () => productService.getCategories(),
    })

    const createProductMutation = useMutation({
        mutationFn: productService.createProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] })
            toast.success('Ürün başarıyla eklendi')
        },
        onError: (error: any) => {
            console.error('API Error Details:', error.response?.data);
            const expressError = error.response?.data?.errors?.[0];
            const message = expressError ? `${expressError.path}: ${expressError.msg}` : error.response?.data?.message || 'Ürün eklenirken bir hata oluştu'
            toast.error(message)
        },
    })

    const updateProductMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Product> }) =>
            productService.updateProduct(id, data),
        onMutate: async ({ id, data }) => {
            await queryClient.cancelQueries({ queryKey: ['products'] })
            const previousProducts = queryClient.getQueryData<any>(['products'])

            queryClient.setQueryData(['products'], (old: any) => {
                if (!old?.items) return old
                return {
                    ...old,
                    items: old.items.map((p: Product) =>
                        p._id === id || p.id === id ? { ...p, ...data } : p
                    )
                }
            })

            return { previousProducts }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] })
            toast.success('Ürün güncellendi')
        },
        onError: (error: any, _variables, context) => {
            console.error('API Error Details:', error.response?.data);
            if (context?.previousProducts) {
                queryClient.setQueryData(['products'], context.previousProducts)
            }
            const expressError = error.response?.data?.errors?.[0];
            const message = expressError ? `${expressError.path}: ${expressError.msg}` : error.response?.data?.message || 'Güncelleme sırasında bir hata oluştu'
            toast.error(message)
        },
    })

    const deleteProductMutation = useMutation({
        mutationFn: productService.deleteProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] })
            toast.success('Ürün silindi')
        },
        onError: () => toast.error('Silme işlemi sırasında bir hata oluştu'),
    })

    const createCategoryMutation = useMutation({
        mutationFn: productService.createCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast.success('Kategori başarıyla eklendi')
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Kategori eklenirken bir hata oluştu'
            toast.error(message)
        },
    })

    const updateCategoryMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Category> }) =>
            productService.updateCategory(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast.success('Kategori güncellendi')
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Kategori güncellenirken bir hata oluştu'
            toast.error(message)
        },
    })

    const deleteCategoryMutation = useMutation({
        mutationFn: productService.deleteCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] })
            toast.success('Kategori silindi')
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Silme işlemi sırasında bir hata oluştu'
            toast.error(message)
        },
    })

    return {
        products: productsQuery.data?.items || [],
        total: productsQuery.data?.total || 0,
        categories: categoriesQuery.data || [],
        isLoading: productsQuery.isLoading || categoriesQuery.isLoading,
        isError: productsQuery.isError || categoriesQuery.isError,
        createProduct: createProductMutation.mutateAsync,
        updateProduct: updateProductMutation.mutateAsync,
        deleteProduct: deleteProductMutation.mutateAsync,
        createCategory: createCategoryMutation.mutateAsync,
        updateCategory: updateCategoryMutation.mutateAsync,
        deleteCategory: deleteCategoryMutation.mutateAsync,
    }
}
